import useEmblaCarousel from 'embla-carousel-react'
import { FC } from 'react'

import { Text, Carousel, ProductCard, clamp } from '@lounge-fe/ui-kit'

import { RecommendationsProps } from './types'
import { useRecommendations } from '../hooks/useRecommendations'
import { RecommendedProduct } from './RecommendedProduct'
import { withErrorBoundary } from '@sentry/react'

const MIN_RECOMMENDATIONS_LIMIT = 0
const MAX_RECOMMENDATIONS_LIMIT = 10

export const Recommendations: FC<RecommendationsProps> = withErrorBoundary(
  ({
    productId,
    title,
    minRecommendations = MIN_RECOMMENDATIONS_LIMIT,
    maxRecommendations = MAX_RECOMMENDATIONS_LIMIT,
    filterProducts = [],
  }) => {
    const { data = [], isLoading } = useRecommendations({
      id: productId,
      filterProducts,
      minRecommendations: clamp(
        minRecommendations,
        MIN_RECOMMENDATIONS_LIMIT,
        maxRecommendations,
      ),
      maxRecommendations: clamp(
        maxRecommendations,
        minRecommendations,
        MAX_RECOMMENDATIONS_LIMIT,
      ),
    })

    const [emblaRef, embla] = useEmblaCarousel({
      align: 'start',
      containScroll: 'trimSnaps',
    })

    if (!isLoading && data.length === 0) {
      return null
    }

    return (
      <div
        className="px-6 py-4 sm:py-0 sm:px-0 mb-20 overflow-x-hidden"
        data-testid="recommendations"
        aria-busy={isLoading}
      >
        {isLoading ? (
          <div className="auto-cols-[calc(75%-12px)] sm:auto-cols-[calc(50%-12px)] md:auto-cols-[calc(33.33%-12px)] xl:auto-cols-[calc(25%-12px)] gap-x-4">
            <ProductCard.Skeleton />
          </div>
        ) : (
          <Carousel emblaRef={emblaRef} embla={embla}>
            <div className="flex items-center justify-between mb-10">
              <Text variant="title-five">{title}</Text>
              <Carousel.Arrows className="hidden sm:flex" />
            </div>
            <Carousel.Viewport showOverflow className="md:overflow-hidden">
              <Carousel.Container className="auto-cols-[calc(75%-12px)] sm:auto-cols-[calc(50%-12px)] md:auto-cols-[calc(33.33%-12px)] xl:auto-cols-[calc(25%-12px)] gap-x-4">
                {data.map((product, idx) => (
                  <Carousel.Slide key={product.id} index={idx}>
                    <RecommendedProduct product={product} />
                  </Carousel.Slide>
                ))}
              </Carousel.Container>
            </Carousel.Viewport>
          </Carousel>
        )}
      </div>
    )
  },
  { fallback: <></> },
)
